import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError, formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, X, Save, Package } from "lucide-react";
import { toast } from "sonner";
import LotPickerModal from "@/components/LotPickerModal";

const HAS_ITEMS = ["Sales", "Purchase", "Debit Note", "Credit Note"];
const OUTWARD_TYPES = ["Sales", "Debit Note"];  // require lot picks under Specific-Id

// ── Ledger-group restrictions per voucher type & Dr/Cr side ────────────
// (Payment / Receipt / Contra restrict one or both sides to Cash & Bank only)
const CASH_BANK_GROUPS = new Set(["Cash-in-Hand", "Bank Accounts", "Bank OD"]);
function allowedForSide(voucherType, drCr) {
  // Returns null (no restriction) or a Set of group names allowed on that side.
  if (voucherType === "Contra") return CASH_BANK_GROUPS;                   // both sides
  if (voucherType === "Payment" && drCr === "Cr") return CASH_BANK_GROUPS; // cash/bank pays out
  if (voucherType === "Receipt" && drCr === "Dr") return CASH_BANK_GROUPS; // cash/bank receives
  return null;                                                              // Journal / others
}
function filterLedgersForSide(ledgers, voucherType, drCr) {
  const allowed = allowedForSide(voucherType, drCr);
  if (!allowed) return ledgers;
  return ledgers.filter((l) => allowed.has(l.group));
}

function Inner() {
  const params = useParams();
  const { type: routeType, vid } = params;
  const isEdit = Boolean(vid);

  const { activeId } = useCompany();
  const navigate = useNavigate();
  const location = useLocation();
  // When the user lands on this page from a ledger statement (or any other
  // page that passes `state.from`), return them there after Save/Cancel.
  const backTo = location.state?.from && typeof location.state.from === "string" && location.state.from.startsWith("/")
    ? location.state.from
    : "/vouchers";

  const [ledgers, setLedgers] = useState([]);
  const [items, setItems] = useState([]);
  const [type, setType] = useState(routeType || "Payment");
  const [loadingVoucher, setLoadingVoucher] = useState(isEdit);
  const isInventory = HAS_ITEMS.includes(type);
  // Cached from GET /companies/{cid} — drives the Specific-Id lot picker.
  const [valuationMethod, setValuationMethod] = useState("FIFO");
  const isSpecificId = valuationMethod === "SPECIFIC_IDENTIFICATION";
  const needsLots = isSpecificId && OUTWARD_TYPES.includes(type);
  // Lot picker modal state — { idx, itemId, itemName, unit, qty, initial }
  const [lotPicker, setLotPicker] = useState(null);

  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    voucher_no: "",
    reference: "",
    narration: "",
    party_ledger_id: "",
    party_ledger_name: "",
    cgst: 0,
    sgst: 0,
    igst: 0,
    // Rule 46 Ship 2 — document variant fields
    doc_variant: "",                  // ""="Tax Invoice" (Sales default), "bill_of_supply"
    reason: "",                       // Credit / Debit Note — Section 34 reason
    is_advance: false,                // Receipt voucher — Rule 50 advance receipt
    refund_against_voucher_no: "",    // Payment voucher — Rule 51 refund
    original_invoice_no: "",          // Credit / Debit Note reference
    original_invoice_date: "",
  });
  const [entries, setEntries] = useState(() => [
    { _rid: `r-${Date.now()}-1`, ledger_id: "", ledger_name: "", dr_cr: "Dr", amount: 0 },
    { _rid: `r-${Date.now()}-2`, ledger_id: "", ledger_name: "", dr_cr: "Cr", amount: 0 },
  ]);
  const [stockEntries, setStockEntries] = useState(() => [
    { _rid: `s-${Date.now()}-1`, item_id: "", item_name: "", qty: 1, rate: 0, unit: "Nos", amount: 0, hsn: "", gst_rate: 18, discount_pct: 0, discount_amount: 0 },
  ]);
  const [cheque, setCheque] = useState({
    enabled: false,
    cheque_no: "",
    bank_name: "",
    payee: "",
    issue_date: "",
    status: "Pending",
  });
  // Immediate cash settlement — Sales: auto-creates Receipt; Purchase: auto-creates Payment.
  const [immediateCash, setImmediateCash] = useState({
    enabled: false,
    ledger_id: "",   // "" → server uses default Cash A/c under Cash-in-Hand
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!activeId) return;
    (async () => {
      const [L, I, C] = await Promise.all([
        api.get(`/companies/${activeId}/ledgers`),
        api.get(`/companies/${activeId}/items`),
        api.get(`/companies/${activeId}`),
      ]);
      setLedgers(L.data);
      setItems(I.data);
      setValuationMethod(C.data?.inventory_valuation_method || "FIFO");
    })();
  }, [activeId]);

  // Load existing voucher when editing
  useEffect(() => {
    if (!isEdit || !vid) return;
    let mounted = true;
    setLoadingVoucher(true);
    const apply = (v) => {
      if (!mounted) return;
      setType(v.voucher_type);
      setForm({
        date: v.date,
        voucher_no: v.voucher_no || "",
        reference: v.reference || "",
        narration: v.narration || "",
        party_ledger_id: v.party_ledger_id || "",
        party_ledger_name: v.party_ledger_name || "",
        cgst: v.cgst || 0,
        sgst: v.sgst || 0,
        igst: v.igst || 0,
        doc_variant: v.doc_variant || "",
        reason: v.reason || "",
        is_advance: Boolean(v.is_advance),
        refund_against_voucher_no: v.refund_against_voucher_no || "",
        original_invoice_no: v.original_invoice_no || "",
        original_invoice_date: v.original_invoice_date || "",
      });
      setEntries(
        (v.entries || []).length
          ? v.entries.map((e, i) => ({ _rid: `r-load-${i}`, ...e }))
          : [
              { _rid: `r-${Date.now()}-1`, ledger_id: "", ledger_name: "", dr_cr: "Dr", amount: 0 },
              { _rid: `r-${Date.now()}-2`, ledger_id: "", ledger_name: "", dr_cr: "Cr", amount: 0 },
            ]
      );
      setStockEntries(
        (v.items || []).length
          ? v.items.map((it, i) => ({ _rid: `s-load-${i}`, ...it }))
          : [{ _rid: `s-${Date.now()}-1`, item_id: "", item_name: "", qty: 1, rate: 0, unit: "Nos", amount: 0, hsn: "", gst_rate: 18, discount_pct: 0, discount_amount: 0 }]
      );
      if (v.cheque_details) {
        setCheque({
          enabled: true,
          cheque_no: v.cheque_details.cheque_no || "",
          bank_name: v.cheque_details.bank_name || "",
          payee: v.cheque_details.payee || "",
          issue_date: v.cheque_details.issue_date || "",
          status: v.cheque_details.status || "Pending",
        });
      }
    };
    api.get(`/vouchers/${vid}`)
      .then(({ data: v }) => apply(v))
      .catch((err) => {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not load voucher");
        navigate(backTo);
      })
      .finally(() => { if (mounted) setLoadingVoucher(false); });
    return () => { mounted = false; };
  }, [isEdit, vid, navigate, backTo]);

  const itemsTotal = useMemo(() =>
    stockEntries.reduce((s, e) => s + (Number(e.amount) || 0), 0),
  [stockEntries]);

  const entriesTotal = useMemo(() =>
    entries.reduce((s, e) => s + (e.dr_cr === "Dr" ? Number(e.amount) || 0 : 0), 0),
  [entries]);

  // ── Double-entry balance (manual vouchers) ─────────────────────────────
  // Debit total must equal Credit total before a voucher can be saved.
  const drTotal = useMemo(() =>
    entries.reduce((s, e) => s + (e.dr_cr === "Dr" ? Number(e.amount) || 0 : 0), 0),
  [entries]);
  const crTotal = useMemo(() =>
    entries.reduce((s, e) => s + (e.dr_cr === "Cr" ? Number(e.amount) || 0 : 0), 0),
  [entries]);
  const balanceDiff = +(drTotal - crTotal).toFixed(2);
  const isBalanced = Math.abs(balanceDiff) < 0.01;

  const total = isInventory
    ? itemsTotal + Number(form.cgst || 0) + Number(form.sgst || 0) + Number(form.igst || 0)
    : entriesTotal;

  const updateEntry = (idx, key, val) => {
    const arr = [...entries];
    if (key === "ledger_id") {
      const led = ledgers.find((l) => l.id === val);
      arr[idx] = { ...arr[idx], ledger_id: val, ledger_name: led?.name || "" };
    } else {
      arr[idx] = { ...arr[idx], [key]: val };
    }
    setEntries(arr);
  };

  /**
   * Discount has TWO inputs — Disc % and Disc ₹ — bound two-way:
   * editing the % derives the ₹, editing the ₹ derives the %, and either
   * recomputes Amount = qty*rate − ₹.  Lets the user enter whichever is more
   * natural for the deal (e.g. supplier offers "₹500 off" instead of a %).
   */
  const updateStock = (idx, key, val) => {
    const arr = [...stockEntries];
    if (key === "item_id") {
      const it = items.find((i) => i.id === val);
      arr[idx] = {
        ...arr[idx],
        item_id: val,
        item_name: it?.name || "",
        unit: it?.unit || "Nos",
        hsn: it?.hsn || "",
        gst_rate: it?.gst_rate || 0,
        rate: it?.opening_rate || arr[idx].rate,
      };
    } else {
      arr[idx] = { ...arr[idx], [key]: val };
    }
    const qty = Number(arr[idx].qty) || 0;
    const rate = Number(arr[idx].rate) || 0;
    const gross = qty * rate;

    // Reconcile the two discount inputs depending on which the user just edited.
    if (key === "discount_pct") {
      const pct = Math.max(0, Math.min(100, Number(arr[idx].discount_pct) || 0));
      arr[idx].discount_pct = pct;
      arr[idx].discount_amount = +(gross * pct / 100).toFixed(2);
    } else if (key === "discount_amount") {
      const amt = Math.max(0, Math.min(gross, Number(arr[idx].discount_amount) || 0));
      arr[idx].discount_amount = +amt.toFixed(2);
      arr[idx].discount_pct = gross > 0 ? +(amt * 100 / gross).toFixed(2) : 0;
    } else {
      // qty / rate / item changed — re-apply the % the user previously chose,
      // since "10% off" should follow gross changes; the ₹ amount is derived.
      const pct = Number(arr[idx].discount_pct) || 0;
      arr[idx].discount_amount = +(gross * pct / 100).toFixed(2);
    }
    arr[idx].amount = +(gross - (Number(arr[idx].discount_amount) || 0)).toFixed(2);
    setStockEntries(arr);
  };

  const autoFillGST = () => {
    const taxable = itemsTotal;
    // Pick avg gst rate from items
    const rates = stockEntries.filter((e) => e.gst_rate).map((e) => Number(e.gst_rate));
    const rate = rates.length ? rates.reduce((a, b) => a + b, 0) / rates.length : 18;
    const half = (taxable * rate) / 200;
    setForm({ ...form, cgst: half.toFixed(2), sgst: half.toFixed(2), igst: 0 });
  };

  const save = async () => {
    // ── Pre-save validation ────────────────────────────────────────
    // 1) Inventory vouchers must have a party ledger.
    //    Sales / Credit-Note → party sits on Dr side; missing party =
    //    "corresponding debit ledger". Purchase / Debit-Note → party sits on
    //    Cr side; missing party = "corresponding credit ledger".
    if (isInventory && !form.party_ledger_id) {
      const missingSide = (type === "Sales" || type === "Credit Note") ? "debit" : "credit";
      toast.error(`Please select corresponding ${missingSide} ledger`);
      return;
    }
    // 2) At least one row per Dr and Cr side must have a valid ledger + amount.
    //    Non-inventory only — inventory vouchers auto-generate `entries`.
    if (!isInventory) {
      const validEntries = entries.filter((e) => e.ledger_id && Number(e.amount) > 0);
      const drOk = validEntries.some((e) => e.dr_cr === "Dr");
      const crOk = validEntries.some((e) => e.dr_cr === "Cr");
      if (!drOk) {
        toast.error("Please select corresponding debit ledger");
        return;
      }
      if (!crOk) {
        toast.error("Please select corresponding credit ledger");
        return;
      }
      // 3) Enforce per-voucher-type ledger-group restrictions server-side too.
      for (const e of validEntries) {
        const allowed = allowedForSide(type, e.dr_cr);
        if (!allowed) continue;
        const led = ledgers.find((l) => l.id === e.ledger_id);
        if (!led || !allowed.has(led.group)) {
          toast.error(
            `${type} voucher — ${e.dr_cr === "Cr" ? "credit" : "debit"} side must be a Cash or Bank account`,
          );
          return;
        }
      }
      // 4) Double-entry guard — total Debit must equal total Credit.
      if (!isBalanced) {
        const side = balanceDiff > 0 ? "Debit" : "Credit";
        toast.error(
          `Voucher is not balanced. Debit ₹${formatINR(drTotal)} ≠ Credit ₹${formatINR(crTotal)} — ${side} side is higher by ₹${formatINR(Math.abs(balanceDiff))}. Adjust the entries so both sides are equal.`,
        );
        return;
      }
    }
    const payload = {
      voucher_type: type,
      date: form.date,
      voucher_no: form.voucher_no || "",
      reference: form.reference,
      narration: form.narration,
      party_ledger_id: form.party_ledger_id,
      party_ledger_name: form.party_ledger_name,
      entries: isInventory ? [] : entries.filter((e) => e.ledger_id && e.amount > 0).map((e) => ({ ...e, amount: Number(e.amount) })),
      items: isInventory ? stockEntries.filter((e) => e.item_name && e.qty > 0).map((e) => ({
        ...e, qty: Number(e.qty), rate: Number(e.rate), amount: Number(e.amount),
        gst_rate: Number(e.gst_rate || 0),
        discount_pct: Number(e.discount_pct || 0),
        discount_amount: Number(e.discount_amount || 0),
        // Specific-Id: forward the picks the user made in the modal. Backend
        // validates presence + sums for outward types under this method.
        lot_selections: needsLots ? (e.lot_selections || []) : undefined,
      })) : [],
      cgst: Number(form.cgst) || 0,
      sgst: Number(form.sgst) || 0,
      igst: Number(form.igst) || 0,
      total,
      // Rule 46 Ship 2 — document-variant metadata (only sent when relevant)
      doc_variant: (type === "Sales" && form.doc_variant === "bill_of_supply") ? "bill_of_supply" : "",
      reason: (type === "Credit Note" || type === "Debit Note") ? (form.reason || "") : "",
      is_advance: type === "Receipt" ? Boolean(form.is_advance) : false,
      refund_against_voucher_no: type === "Payment" ? (form.refund_against_voucher_no || "") : "",
      original_invoice_no: (type === "Credit Note" || type === "Debit Note") ? (form.original_invoice_no || "") : "",
      original_invoice_date: (type === "Credit Note" || type === "Debit Note") ? (form.original_invoice_date || "") : "",
      cheque_details: cheque.enabled
        ? {
            cheque_no: cheque.cheque_no,
            bank_name: cheque.bank_name,
            payee: cheque.payee || form.party_ledger_name,
            issue_date: cheque.issue_date || form.date,
            status: cheque.status || "Pending",
          }
        : null,
      // Immediate cash settlement — server auto-creates a linked Receipt (Sales)
      // or Payment (Purchase) voucher clearing the party's cash / bank leg.
      immediate_cash_settlement: (type === "Sales" || type === "Purchase") && immediateCash.enabled,
      settlement_ledger_id: (type === "Sales" || type === "Purchase") && immediateCash.enabled ? (immediateCash.ledger_id || "") : "",
    };
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/vouchers/${vid}`, payload);
        toast.success(`${type} voucher updated`);
      } else {
        await api.post(`/companies/${activeId}/vouchers`, payload);
        toast.success(`${type} voucher saved`);
      }
      navigate(backTo);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        title={isEdit ? `Edit ${type} Voucher${form.voucher_no ? ` — ${form.voucher_no}` : ""}` : `New ${type} Voucher`}
        subtitle={loadingVoucher ? "Loading voucher…" : "Fill in the entries. Use Tab to move between fields."}
        actions={
          <>
            <Button variant="outline" onClick={() => navigate(backTo)} className="h-8">Cancel</Button>
            <Button onClick={save} disabled={saving || loadingVoucher} className="bg-blue-700 hover:bg-blue-800 h-8" data-testid="btn-save-voucher">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : isEdit ? "Update" : "Save"}
            </Button>
          </>
        }
      />
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 border border-gray-200 rounded bg-white p-4">
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-gray-600">Date</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="input-voucher-date" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-gray-600">Voucher No.</Label>
            <Input placeholder="Auto" value={form.voucher_no} onChange={(e) => setForm({ ...form, voucher_no: e.target.value })} data-testid="input-voucher-no" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-gray-600">Reference</Label>
            <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
          </div>
          <div className="space-y-1 md:col-span-1">
            <Label className="text-xs uppercase tracking-wider text-gray-600">Party Ledger</Label>
            <select
              value={form.party_ledger_id}
              onChange={(e) => {
                const led = ledgers.find((l) => l.id === e.target.value);
                setForm({ ...form, party_ledger_id: e.target.value, party_ledger_name: led?.name || "" });
              }}
              className="h-8 w-full rounded border border-gray-300 px-2 text-sm bg-white"
              data-testid="select-party-ledger"
            >
              <option value="">— None —</option>
              {ledgers.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        </div>

        {!isInventory ? (
          <div className="border border-gray-200 rounded bg-white overflow-hidden">
            <table className="tx-table">
              <thead>
                <tr><th style={{ width: "55%" }}>Ledger</th><th>Dr/Cr</th><th className="text-right">Amount</th><th></th></tr>
              </thead>
              <tbody>
                {entries.map((e, idx) => {
                  const filteredLedgers = filterLedgersForSide(ledgers, type, e.dr_cr);
                  const restricted = filteredLedgers.length !== ledgers.length;
                  return (
                    <tr key={e._rid}>
                      <td>
                        <select
                          value={e.ledger_id}
                          onChange={(ev) => updateEntry(idx, "ledger_id", ev.target.value)}
                          className="h-8 w-full rounded border border-gray-300 px-2 text-sm bg-white"
                          data-testid={`select-entry-ledger-${idx}`}
                        >
                          <option value="">— Select ledger —</option>
                          {filteredLedgers.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.group})</option>)}
                        </select>
                        {restricted && (
                          <div className="text-[10px] text-gray-500 mt-0.5">
                            {type} · {e.dr_cr === "Cr" ? "Credit" : "Debit"} side — Cash / Bank accounts only
                          </div>
                        )}
                      </td>
                      <td>
                        <select
                          value={e.dr_cr}
                          onChange={(ev) => {
                            // Switching Dr/Cr may change allowed ledgers — clear the
                            // ledger if it's no longer valid on the new side.
                            const newSide = ev.target.value;
                            const allowed = allowedForSide(type, newSide);
                            const led = ledgers.find((l) => l.id === e.ledger_id);
                            const stillValid = !allowed || (led && allowed.has(led.group));
                            setEntries((rows) => rows.map((r, i) => i === idx ? {
                              ...r,
                              dr_cr: newSide,
                              ledger_id: stillValid ? r.ledger_id : "",
                              ledger_name: stillValid ? r.ledger_name : "",
                            } : r));
                          }}
                          className="h-8 rounded border border-gray-300 px-2 text-sm bg-white"
                        >
                          <option value="Dr">Dr</option><option value="Cr">Cr</option>
                        </select>
                      </td>
                      <td>
                        <Input type="number" className="text-right font-mono" value={e.amount} onChange={(ev) => updateEntry(idx, "amount", ev.target.value)} data-testid={`input-entry-amount-${idx}`} />
                      </td>
                      <td>
                        <button onClick={() => setEntries(entries.filter((_, i) => i !== idx))} className="text-red-600"><X className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  );
                })}
                <tr>
                  <td colSpan={4}>
                    <button onClick={() => setEntries([...entries, { _rid: `r-${Date.now()}-${Math.random()}`, ledger_id: "", ledger_name: "", dr_cr: "Dr", amount: 0 }])} className="text-blue-700 text-sm flex items-center gap-1 hover:underline" data-testid="btn-add-entry">
                      <Plus className="w-3.5 h-3.5" /> Add line
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            {/* Live double-entry balance — Debit must equal Credit to save */}
            <div
              className={`flex flex-wrap items-center justify-end gap-6 border-t px-4 py-2.5 text-sm ${
                isBalanced ? "border-gray-200 bg-gray-50" : "border-red-200 bg-red-50"
              }`}
              data-testid="voucher-balance-bar"
            >
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wider text-gray-500">Debit Total</span>
                <span className="font-mono font-semibold tabular-nums text-gray-900" data-testid="voucher-dr-total">₹ {formatINR(drTotal)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wider text-gray-500">Credit Total</span>
                <span className="font-mono font-semibold tabular-nums text-gray-900" data-testid="voucher-cr-total">₹ {formatINR(crTotal)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wider text-gray-500">Difference</span>
                {isBalanced ? (
                  <span className="font-mono font-semibold tabular-nums text-emerald-700" data-testid="voucher-diff">Balanced ✓</span>
                ) : (
                  <span className="font-mono font-semibold tabular-nums text-red-700" data-testid="voucher-diff">
                    ₹ {formatINR(Math.abs(balanceDiff))} {balanceDiff > 0 ? "(Dr higher)" : "(Cr higher)"}
                  </span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="border border-gray-200 rounded bg-white overflow-hidden">
            {needsLots && (
              <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900 flex items-center gap-2" data-testid="specific-id-banner">
                <Package className="w-4 h-4 text-amber-700" />
                Company uses <b>Specific Identification</b> — pick which purchase lot each item is sold from
                using the <b>Lots</b> button on every row.
              </div>
            )}
            <table className="tx-table">
              <thead>
                <tr>
                  <th style={{ width: "22%" }}>Item</th><th style={{ width: "11%" }}>HSN</th>
                  <th className="text-right" style={{ width: "80px" }}>Qty</th>
                  <th style={{ width: "70px" }}>Unit</th>
                  <th className="text-right" style={{ width: "100px" }}>Rate</th>
                  <th className="text-right" style={{ width: "80px" }}>Disc&nbsp;%</th>
                  <th className="text-right" style={{ width: "100px" }}>Disc&nbsp;(₹)</th>
                  <th className="text-right" style={{ width: "110px" }}>Amount</th>
                  {needsLots && <th style={{ width: "90px" }}>Lots</th>}
                  <th style={{ width: "30px" }}></th>
                </tr>
              </thead>
              <tbody>
                {stockEntries.map((e, idx) => (
                  <tr key={e._rid}>
                    <td>
                      {items.length > 0 ? (
                        <select value={e.item_id} onChange={(ev) => updateStock(idx, "item_id", ev.target.value)} className="h-8 w-full rounded border border-gray-300 px-2 text-sm bg-white" data-testid={`select-item-${idx}`}>
                          <option value="">— Select item —</option>
                          {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                        </select>
                      ) : (
                        <Input value={e.item_name} onChange={(ev) => updateStock(idx, "item_name", ev.target.value)} placeholder="Item name" />
                      )}
                    </td>
                    <td><Input value={e.hsn} onChange={(ev) => updateStock(idx, "hsn", ev.target.value)} className="font-mono text-xs" data-testid={`input-hsn-${idx}`} /></td>
                    <td><Input type="number" value={e.qty} onChange={(ev) => updateStock(idx, "qty", ev.target.value)} className="text-right font-mono" data-testid={`input-qty-${idx}`} /></td>
                    <td><Input value={e.unit} onChange={(ev) => updateStock(idx, "unit", ev.target.value)} className="text-xs" /></td>
                    <td><Input type="number" value={e.rate} onChange={(ev) => updateStock(idx, "rate", ev.target.value)} className="text-right font-mono" data-testid={`input-rate-${idx}`} /></td>
                    <td>
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        max={100}
                        value={e.discount_pct ?? 0}
                        onChange={(ev) => updateStock(idx, "discount_pct", ev.target.value)}
                        className="text-right font-mono"
                        placeholder="0"
                        data-testid={`input-discount-pct-${idx}`}
                      />
                    </td>
                    <td>
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        value={e.discount_amount ?? 0}
                        onChange={(ev) => updateStock(idx, "discount_amount", ev.target.value)}
                        className="text-right font-mono"
                        placeholder="0.00"
                        data-testid={`input-discount-amount-${idx}`}
                      />
                    </td>
                    <td className="tx-num font-medium" data-testid={`amount-${idx}`}>{formatINR(e.amount)}</td>
                    {needsLots && (
                      <td>
                        {e.item_id ? (
                          <button
                            onClick={() => setLotPicker({
                              idx,
                              itemId: e.item_id,
                              itemName: e.item_name,
                              unit: e.unit || "Nos",
                              qty: Number(e.qty) || 0,
                              initial: e.lot_selections || [],
                            })}
                            className={`px-2 py-1 rounded text-xs font-medium border ${
                              (e.lot_selections || []).length > 0
                                ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                                : "bg-amber-50 border-amber-300 text-amber-800"
                            } hover:brightness-95`}
                            data-testid={`btn-pick-lots-${idx}`}
                          >
                            {(e.lot_selections || []).length > 0
                              ? `${e.lot_selections.length} lot(s)`
                              : "Pick lots"}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">Select item first</span>
                        )}
                      </td>
                    )}
                    <td><button onClick={() => setStockEntries(stockEntries.filter((_, i) => i !== idx))} className="text-red-600"><X className="w-4 h-4" /></button></td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={needsLots ? 10 : 9}>
                    <button onClick={() => setStockEntries([...stockEntries, { _rid: `s-${Date.now()}-${Math.random()}`, item_id: "", item_name: "", qty: 1, rate: 0, unit: "Nos", amount: 0, hsn: "", gst_rate: 18, discount_pct: 0, discount_amount: 0 }])} className="text-blue-700 text-sm flex items-center gap-1 hover:underline" data-testid="btn-add-item">
                      <Plus className="w-3.5 h-3.5" /> Add item
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="border-t border-gray-200 p-4 grid grid-cols-2 md:grid-cols-4 gap-3 bg-gray-50">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider text-gray-600">CGST</Label>
                <Input type="number" value={form.cgst} onChange={(e) => setForm({ ...form, cgst: e.target.value })} className="text-right font-mono" data-testid="input-cgst" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider text-gray-600">SGST</Label>
                <Input type="number" value={form.sgst} onChange={(e) => setForm({ ...form, sgst: e.target.value })} className="text-right font-mono" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider text-gray-600">IGST</Label>
                <Input type="number" value={form.igst} onChange={(e) => setForm({ ...form, igst: e.target.value })} className="text-right font-mono" />
              </div>
              <div className="flex items-end">
                <Button variant="outline" onClick={autoFillGST} className="h-8 w-full" data-testid="btn-auto-gst">Auto GST</Button>
              </div>
            </div>
          </div>
        )}

        <div className="border border-gray-200 rounded bg-white p-4 flex items-end justify-between gap-4">
          <div className="flex-1 space-y-1">
            <Label className="text-xs uppercase tracking-wider text-gray-600">Narration</Label>
            <Textarea value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} rows={2} data-testid="input-narration" />
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-gray-500">Total</div>
            <div className="font-mono text-2xl font-semibold text-gray-900 tabular-nums" data-testid="voucher-total">
              ₹ {formatINR(total)}
            </div>
          </div>
        </div>

        {/* ── Rule 46 Ship 2 — Document Variant Selector ─────────────── */}
        {type === "Sales" && (
          <div
            className="border border-amber-200 rounded bg-amber-50/40 p-4"
            data-testid="panel-doc-variant"
          >
            <Label className="text-xs uppercase tracking-wider text-amber-800 font-semibold">
              Document Type
            </Label>
            <div className="mt-2 flex gap-3 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={form.doc_variant !== "bill_of_supply"}
                  onChange={() => setForm({ ...form, doc_variant: "" })}
                  data-testid="radio-tax-invoice"
                />
                <span>Tax Invoice <span className="text-xs text-gray-500">(Rule 46 — default)</span></span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={form.doc_variant === "bill_of_supply"}
                  onChange={() => setForm({ ...form, doc_variant: "bill_of_supply" })}
                  data-testid="radio-bill-of-supply"
                />
                <span>Bill of Supply <span className="text-xs text-gray-500">(Rule 49 — composition / exempt)</span></span>
              </label>
            </div>
            {form.doc_variant === "bill_of_supply" && (
              <div className="mt-2 text-xs text-amber-800 italic">
                A Bill of Supply will be printed without any GST breakup. Use this only if you are
                a composition dealer or supplying exempt goods/services.
              </div>
            )}
          </div>
        )}

        {(type === "Credit Note" || type === "Debit Note") && (
          <div
            className="border border-red-200 rounded bg-red-50/30 p-4 space-y-3"
            data-testid="panel-credit-debit-note"
          >
            <div className="text-xs uppercase tracking-wider text-red-800 font-semibold">
              {type === "Credit Note" ? "Credit Note" : "Debit Note"} — Section 34 / Rule 53 details
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider text-gray-600">Original Invoice No.</Label>
                <Input
                  value={form.original_invoice_no}
                  onChange={(e) => setForm({ ...form, original_invoice_no: e.target.value })}
                  placeholder="INV-00001"
                  data-testid="input-original-invoice-no"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider text-gray-600">Original Invoice Date</Label>
                <Input
                  type="date"
                  value={form.original_invoice_date}
                  onChange={(e) => setForm({ ...form, original_invoice_date: e.target.value })}
                  data-testid="input-original-invoice-date"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-gray-600">
                Reason for issue (required per Section 34)
              </Label>
              <Textarea
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                rows={2}
                placeholder="e.g. Price reduction agreed post-issue; goods returned by buyer; deficiency in service…"
                data-testid="input-reason"
              />
            </div>
          </div>
        )}

        {type === "Receipt" && (
          <div className="border border-yellow-200 rounded bg-yellow-50/40 p-4" data-testid="panel-advance-receipt">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-900 cursor-pointer">
              <input
                type="checkbox"
                checked={Boolean(form.is_advance)}
                onChange={(e) => setForm({ ...form, is_advance: e.target.checked })}
                data-testid="check-is-advance"
              />
              <span>
                Mark as Advance Receipt
                <span className="text-xs text-gray-500 ml-1">(Rule 50 — tax liability on advance)</span>
              </span>
            </label>
            {form.is_advance && (
              <div className="mt-2 text-xs text-yellow-800 italic">
                The printed Receipt Voucher will show an "Advance Receipt" heading. Remember to
                discharge GST on this advance and adjust against the final Tax Invoice once supplies are made.
              </div>
            )}
          </div>
        )}

        {type === "Payment" && (
          <div className="border border-red-200 rounded bg-red-50/30 p-4" data-testid="panel-refund">
            <Label className="text-xs uppercase tracking-wider text-gray-600">
              Refund against advance Receipt Voucher No. <span className="text-gray-400">(optional)</span>
            </Label>
            <Input
              value={form.refund_against_voucher_no}
              onChange={(e) => setForm({ ...form, refund_against_voucher_no: e.target.value })}
              placeholder="e.g. RCT-00007"
              className="mt-1 max-w-sm"
              data-testid="input-refund-against"
            />
            {form.refund_against_voucher_no && (
              <div className="mt-2 text-xs text-red-700 italic">
                This payment will be printed as a "Refund Voucher" (Rule 51) referencing the
                original advance receipt.
              </div>
            )}
          </div>
        )}


        {(type === "Sales" || type === "Purchase") && (
          <div className="border border-gray-200 rounded bg-white p-4" data-testid="immediate-cash-panel">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-900">
              <input
                type="checkbox"
                checked={immediateCash.enabled}
                onChange={(e) => setImmediateCash({ ...immediateCash, enabled: e.target.checked })}
                data-testid="check-immediate-cash"
              />
              {type === "Sales"
                ? "Immediate cash payment received (auto-posts a Receipt voucher clearing the debtor)"
                : "Immediate cash payment made (auto-posts a Payment voucher clearing the creditor)"}
            </label>
            {immediateCash.enabled && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-gray-600">
                    {type === "Sales" ? "Cash / Bank account receiving payment" : "Cash / Bank account paying"}
                  </Label>
                  <select
                    value={immediateCash.ledger_id}
                    onChange={(e) => setImmediateCash({ ...immediateCash, ledger_id: e.target.value })}
                    className="h-8 w-full rounded border border-gray-300 px-2 text-sm bg-white"
                    data-testid="select-immediate-cash-ledger"
                  >
                    <option value="">— Default: Cash A/c —</option>
                    {ledgers.filter((l) => CASH_BANK_GROUPS.has(l.group)).map((l) => (
                      <option key={l.id} value={l.id}>{l.name} ({l.group})</option>
                    ))}
                  </select>
                  <p className="text-[10px] text-gray-500">
                    Leave blank to auto-use / create <strong>Cash A/c</strong> under Cash-in-Hand.
                  </p>
                </div>
                <div className="text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded p-2 leading-relaxed self-start">
                  On save, a linked <strong>{type === "Sales" ? "Receipt" : "Payment"}</strong> voucher of
                  <strong> ₹{Number(total || 0).toLocaleString("en-IN")}</strong> will be created:
                  <div className="mt-1 font-mono text-[11px]">
                    {type === "Sales"
                      ? <>Dr Cash / Bank &nbsp;→&nbsp; Cr {form.party_ledger_name || "Party"}</>
                      : <>Dr {form.party_ledger_name || "Party"} &nbsp;→&nbsp; Cr Cash / Bank</>}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {(type === "Payment" || type === "Receipt") && (
          <div className="border border-gray-200 rounded bg-white p-4" data-testid="cheque-details-panel">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-900">
              <input
                type="checkbox"
                checked={cheque.enabled}
                onChange={(e) => setCheque({ ...cheque, enabled: e.target.checked })}
                data-testid="check-cheque-enabled"
              />
              Attach Cheque Details (will appear in Cheque Register)
            </label>
            {cheque.enabled && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-gray-600">Cheque No.</Label>
                  <Input value={cheque.cheque_no} onChange={(e) => setCheque({ ...cheque, cheque_no: e.target.value })} className="h-8" data-testid="input-cheque-no" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-gray-600">Bank Name</Label>
                  <Input value={cheque.bank_name} onChange={(e) => setCheque({ ...cheque, bank_name: e.target.value })} className="h-8" data-testid="input-bank-name" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-gray-600">Payee {form.party_ledger_name && <span className="text-gray-400">(defaults to {form.party_ledger_name})</span>}</Label>
                  <Input value={cheque.payee} onChange={(e) => setCheque({ ...cheque, payee: e.target.value })} className="h-8" placeholder={form.party_ledger_name || "Payee name"} data-testid="input-payee" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-gray-600">Issue Date</Label>
                  <Input type="date" value={cheque.issue_date || form.date} onChange={(e) => setCheque({ ...cheque, issue_date: e.target.value })} className="h-8" data-testid="input-issue-date" />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {lotPicker && (
        <LotPickerModal
          open={Boolean(lotPicker)}
          onClose={() => setLotPicker(null)}
          companyId={activeId}
          itemId={lotPicker.itemId}
          itemName={lotPicker.itemName}
          unit={lotPicker.unit}
          lineQty={lotPicker.qty}
          date={form.date}
          excludeVoucherId={isEdit ? vid : undefined}
          initialSelections={lotPicker.initial}
          onSave={(selections) => {
            const next = [...stockEntries];
            next[lotPicker.idx] = { ...next[lotPicker.idx], lot_selections: selections };
            setStockEntries(next);
          }}
        />
      )}
    </>
  );
}

export default function VoucherEntry() {
  return <RequireCompany><Inner /></RequireCompany>;
}
